<script lang="ts" setup>
import { useUnmanagedBusinessStore } from '@/unmanagedBusinessModule/pages/unmanagedBusinessStore';
import { storeToRefs } from 'pinia';
import { i18n } from '@/i18n';
import TextInput from './TextInput.vue';
import InfoModal from '@/corePagesModule/components/InfoModal.vue';
import SimpleSelectInput from '@/corePagesModule/components/SelectInput.vue';
import SelectInput from '@/corePagesModule/SelectInput/SelectInput.vue';
import { computed, ref, watch } from 'vue';
import { useForms } from '@/corePagesModule/composables/useForm';
import { validateRuleKeyMap, selectOptionKeyMap } from '../constants/formKeyMap';
import { UnmanagedBusinessService } from '../domains/services/UnmanagedBusinessService';
import { FieldItem, ValidateRule } from '@/coreDomainsModule/constant/validateRules';

const t = i18n.t;

const store = useUnmanagedBusinessStore();
const { state } = storeToRefs(store);
const { closeModal, openModalByPreviousModal } = store;

const filedItemsKey = [
  'leadTime',
  'leadTimeUnit',
  'MOQ',
  'unit',
  'stdInventoryIndicesMin',
  'stdInventoryIndicesMax',
  'stdInventoryIndicesUnit',
  'manufacturer',
];

const isOpenManufacturerSelectInput = ref(false);

const filedItems = computed((): FieldItem[] => {
  // 選択肢の項目を取得
  const items: FieldItem[] = [];
  filedItemsKey.forEach((item) => {
    items.push({
      name: item,
      rules: validateRuleKeyMap[item],
    });
  });
  return items;
});

const { fieldState, allValidate, resetForm } = useForms(filedItems.value);

/**
 * 次へボタンクリック時の処理
 */
const onNext = () => {
  if (allValidate()) {
    state.value.settingUnmanagedBusiness.leadTime = Number(fieldState['leadTime'].value.value);
    state.value.settingUnmanagedBusiness.leadTimeUnit = UnmanagedBusinessService.convertUnitToSystemUnit(
      fieldState['leadTimeUnit'].value.value
    );
    state.value.settingUnmanagedBusiness.moq = Number(fieldState['MOQ'].value.value);
    state.value.settingUnmanagedBusiness.unit = fieldState['unit'].value.value;
    state.value.settingUnmanagedBusiness.stdInventoryIndices = {
      標準在庫指数: {
        min: Number(fieldState['stdInventoryIndicesMin'].value.value),
        max: Number(fieldState['stdInventoryIndicesMax'].value.value),
        option: {
          unit: UnmanagedBusinessService.convertUnitToSystemUnit(fieldState['stdInventoryIndicesUnit'].value.value),
        },
      },
    };
    state.value.settingUnmanagedBusiness.manufacturer = fieldState['manufacturer'].value.value;
    openModalByPreviousModal('questionUpdateExistBusinessLeadTimeAndStdInventoryIndices');
  }
};

watch(
  () => state.value.modalInfo.isOpen.questionUpdateExistBusinessLeadTimeAndStdInventoryIndices,
  (value) => {
    if (value) {
      resetForm(filedItems.value);
      // settingUnmanagedBusinessの値をfieldStateにセット
      for (let i = 0; i < filedItems.value.length; i++) {
        const name = filedItems.value[i].name;
        fieldState[name].setValue(state.value.modalInfo.modalState.registerLeadTimeAndEtc[name], false);
      }
    }
  }
);

/**
 * バリデーションにRequiredがある場合は*を返す
 * @param key　- 入力項目のキー
 */
const getRequiredMark = (key: string | number) => {
  if (validateRuleKeyMap[key].includes(ValidateRule.Required)) {
    return '*';
  }
  return '';
};
</script>
<template>
  <InfoModal
    :modal-state="state.modalInfo.isOpen.questionUpdateExistBusinessLeadTimeAndStdInventoryIndices"
    :back-button-label="t('キャンセル')"
    :next-button-label="t('次へ')"
    @on-back="closeModal('questionUpdateExistBusinessLeadTimeAndStdInventoryIndices')"
    @on-next="onNext"
  >
    <template #title>{{ t('リードタイム・標準在庫指数・MOQ・単位の登録') }}</template>
    <template #description>{{ t('リードタイム、標準在庫指数、MOQ(最小発注数量)、単位を登録してください。') }}</template>
    <div>
      <p class="modal__required-text">{{ t('*マークは必須') }}</p>
      <div class="modal__selection-container">
        <div class="modal__selection modal__selection--4-column">
          <!-- 置場コード・置き場名・仕入れ先コード・仕入れ先名・商品コード・商品名・容量 を値が入った状態かつdisableで表示する（全部TextInput） -->
          <TextInput
            :model-value="state.modalInfo.modalState.registerLeadTimeAndEtc.warehouseNumber"
            :label="t('取引登録モーダル選択肢:warehouseNumber') + getRequiredMark('warehouseNumber')"
            disabled
          />
          <TextInput
            :model-value="state.modalInfo.modalState.registerLeadTimeAndEtc.warehouseName"
            :label="t('取引登録モーダル選択肢:warehouseName') + getRequiredMark('warehouseName')"
            disabled
          />
          <TextInput
            :model-value="state.modalInfo.modalState.registerLeadTimeAndEtc.supplierNumber"
            :label="t('取引登録モーダル選択肢:supplierNumber') + getRequiredMark('supplierNumber')"
            disabled
          />
          <TextInput
            :model-value="state.modalInfo.modalState.registerLeadTimeAndEtc.supplierName"
            :label="t('取引登録モーダル選択肢:supplierName') + getRequiredMark('supplierName')"
            disabled
          />
        </div>
        <div class="modal__selection modal__selection--5-column">
          <TextInput
            :model-value="state.modalInfo.modalState.registerLeadTimeAndEtc.businessNumber"
            :label="t('取引登録モーダル選択肢:businessNumber') + getRequiredMark('businessNumber')"
            disabled
          />
          <TextInput
            :model-value="state.modalInfo.modalState.registerLeadTimeAndEtc.businessName"
            :label="t('取引登録モーダル選択肢:businessName') + getRequiredMark('businessName')"
            disabled
          />
          <TextInput
            :model-value="state.modalInfo.modalState.registerLeadTimeAndEtc.package"
            :label="t('取引登録モーダル選択肢:package') + getRequiredMark('package')"
            disabled
          />
          <TextInput
            :model-value="state.modalInfo.modalState.registerLeadTimeAndEtc.unit"
            :label="t('取引登録モーダル選択肢:unit') + getRequiredMark('unit')"
            :is-error="fieldState.unit.isError.value"
            :error-message="fieldState.unit.errorMessage.value"
            :placeholder="t('取引登録モーダル選択肢:unit')"
            @update:model-value="(e) => fieldState.unit.setValue(e)"
          />
          <SelectInput
            :model-value="state.modalInfo.modalState.registerLeadTimeAndEtc.manufacturer"
            :filter-type="'MANUFACTURER'"
            :index="1"
            :label="t('取引登録モーダル選択肢:manufacturer') + getRequiredMark('manufacturer')"
            :placeholder="t(`取引登録モーダル選択肢:manufacturer`)"
            :is-open-prop="isOpenManufacturerSelectInput"
            :is-error="fieldState.manufacturer.isError.value"
            :error-message="fieldState.manufacturer.errorMessage.value"
            :prop-options="state.tableInfo[state.selectedFileType].selectOptionInfo['manufacturer']"
            @click:select-input="() => (isOpenManufacturerSelectInput = false)"
            @update:model-value="(e) => fieldState.manufacturer.setValue(e)"
          />
        </div>
      </div>
    </div>
    <div class="modal__selection-container modal__selection modal__new-selection modal__selection-user-input modal__selection--4-column">
      <!-- リードタイム・リードタイム単位・MOQ・単位・標準在庫指数下限・標準在庫指数上限・標準在庫指数単位 の入力項目 -->
      <TextInput
        :model-value="fieldState.leadTime.value.value"
        :label="t('取引登録モーダル選択肢:leadTime') + getRequiredMark('leadTime')"
        :is-error="fieldState.leadTime.isError.value"
        :error-message="fieldState.leadTime.errorMessage.value"
        :placeholder="t('取引登録モーダル選択肢:leadTime')"
        @update:model-value="(e) => fieldState.leadTime.setValue(e)"
      />
      <SimpleSelectInput
        :model-value="fieldState.leadTimeUnit.value.value"
        :index="4"
        :label="t('取引登録モーダル選択肢:leadTimeUnit') + getRequiredMark('leadTimeUnit')"
        :is-error="fieldState.leadTimeUnit.isError.value"
        :error-message="fieldState.leadTimeUnit.errorMessage.value"
        :placeholder="t('取引登録モーダル選択肢:leadTimeUnit')"
        :options="selectOptionKeyMap.leadTimeUnit.map((item) => t(item))"
        @update:model-value="(e) => fieldState.leadTimeUnit.setValue(e)"
      />
      <TextInput
        :model-value="fieldState.MOQ.value.value"
        :label="t('取引登録モーダル選択肢:MOQ') + getRequiredMark('MOQ')"
        :is-error="fieldState.MOQ.isError.value"
        :error-message="fieldState.MOQ.errorMessage.value"
        :placeholder="t('取引登録モーダル選択肢:MOQ')"
        @update:model-value="(e) => fieldState.MOQ.setValue(e)"
      />
      <TextInput
        :model-value="fieldState.stdInventoryIndicesMin.value.value"
        :label="t('取引登録モーダル選択肢:stdInventoryIndicesMin') + getRequiredMark('stdInventoryIndicesMin')"
        :is-error="fieldState.stdInventoryIndicesMin.isError.value"
        :error-message="fieldState.stdInventoryIndicesMin.errorMessage.value"
        :placeholder="t('取引登録モーダル選択肢:stdInventoryIndicesMin')"
        @update:model-value="(e) => fieldState.stdInventoryIndicesMin.setValue(e)"
      />
      <TextInput
        :model-value="fieldState.stdInventoryIndicesMax.value.value"
        :label="t('取引登録モーダル選択肢:stdInventoryIndicesMax') + getRequiredMark('stdInventoryIndicesMax')"
        :is-error="fieldState.stdInventoryIndicesMax.isError.value"
        :error-message="fieldState.stdInventoryIndicesMax.errorMessage.value"
        :placeholder="t('取引登録モーダル選択肢:stdInventoryIndicesMax')"
        @update:model-value="(e) => fieldState.stdInventoryIndicesMax.setValue(e)"
      />
      <SimpleSelectInput
        :model-value="fieldState.stdInventoryIndicesUnit.value.value"
        :index="6"
        :label="t('取引登録モーダル選択肢:stdInventoryIndicesUnit') + getRequiredMark('stdInventoryIndicesUnit')"
        :is-error="fieldState.stdInventoryIndicesUnit.isError.value"
        :error-message="fieldState.stdInventoryIndicesUnit.errorMessage.value"
        :placeholder="t('取引登録モーダル選択肢:stdInventoryIndicesUnit')"
        :options="selectOptionKeyMap.stdInventoryIndicesUnit.map((item) => t(item))"
        @update:model-value="(e) => fieldState.stdInventoryIndicesUnit.setValue(e)"
      />
    </div>
  </InfoModal>
</template>
<style lang="scss" scoped>
.modal {
  display: grid;
  gap: 24px;
  padding: 24px 16px;
  background-color: $semanticWhite;
  border-radius: 16px;
  min-width: 1300px;

  &__sub-title {
    font-family: Noto Sans JP;
    font-size: 16px;
    font-weight: 400;
    line-height: 16px;
    text-align: center;

    margin-bottom: 8px;

    color: $textDefault;
  }

  &__required-text {
    font-family: Noto Sans JP;
    font-size: 14px;
    font-weight: 400;
    line-height: 18.2px;
    text-align: left;
    color: $textDefault;
  }

  &__title {
    font-family: Noto Sans JP;
    font-size: 22px;
    font-weight: 700;
    line-height: 28.6px;
    text-align: center;

    color: $textDefault;
  }

  &__description {
    font-family: Noto Sans JP;
    font-size: 14px;
    font-weight: 400;
    line-height: 18.2px;
    text-align: center;

    color: $textDefault;
  }

  &__selection {
    display: grid;
    column-gap: 8px;
    row-gap: 16px;
    min-width: 1300px;

    &-user-input {
      display: grid;
      grid-template-columns: repeat(3, 1fr);
      column-gap: 8px;
      row-gap: 16px;
    }

    &-container {
      background-color: $backgroundDefault;
      padding: 16px;
      border-radius: 8px;
      display: grid;
      row-gap: 16px;
    }

    &--4-column {
      grid-template-columns: repeat(4, 1fr);
    }
    &--5-column {
      grid-template-columns: repeat(5, 1fr);
    }
  }

  &__new-selection {
    background-color: $backgroundDefault;
    padding: 16px;
    border-radius: 8px;

    font-family: Noto Sans JP;
    font-size: 14px;
    font-weight: 400;
    line-height: 14px;
    text-align: left;

    &__container {
      display: grid;
      grid-template-columns: minmax(160px, auto) auto minmax(160px, auto) auto auto;
      column-gap: 8px;
      justify-content: start;
      align-items: center;
      margin: 8px 0 16px 0;
    }
  }

  &__button {
    min-width: 160px;
    height: 40px;
    font-family: Noto Sans JP;
    font-size: 16px;
    font-weight: 700;
    line-height: 40px;
    letter-spacing: 0em;
    text-align: center;
    border-radius: 4px;
    padding: 0 8px;

    &--cancel {
      color: $semanticPrimary;
      background-color: $semanticWhite;
      border: 1px solid $semanticPrimary;
    }
    &--save {
      color: $semanticWhite;
      background-color: $semanticPrimary;
      border: 1px solid $semanticPrimary;

      &:disabled {
        background-color: $surfaceSecondary;
        border: 1px solid $surfaceSecondary;
        color: $textDisable;
      }
    }

    &-container {
      display: grid;
      gap: 10px;
      grid-template-columns: repeat(2, auto);
      justify-content: center;
      padding-top: 16px;
    }
  }
}
</style>
